import { SOURCE_GRADE_TEXT, type BenefitDto, type ContractSummaryDto, type GapDto, type PlanGoalDto, type ProcessDto, type ProgramDto, type RoleDashboardDto, type RoleDashboardLineDto, type SiteAttributesDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { ErrorText } from '../../../ui';
import { Documento } from './Documento';

const BOX = 'space-y-3 rounded-3xl bg-white/70 p-5 ring-1 ring-black/5 md:p-7';
const ore = (n: number) => `${n.toLocaleString('it-IT', { maximumFractionDigits: 1 })} ore`;
const euroAnno = (n: number | null) => (n === null ? null : `${n.toLocaleString('it-IT')} € l’anno`);
const GAP: Record<GapDto['tipo'], string> = { obbligo_scoperto: 'Obbligo scoperto', lavoro_a_mano: 'Lavoro a mano', persone: 'Persone', impianto_da_verificare: 'Da verificare' };
const STIMA: Record<BenefitDto['stima'], string> = { da_confermare: 'stima da confermare con te', stimata: 'stima', misurata: 'misurato' };

/** Ore ed euro con la formula a portata di mano (contratto, art. 7.13: ogni numero ha il suo calcolo). */
function Benefici({ b, scuro = false }: { b: BenefitDto | null; scuro?: boolean }) {
  if (!b) return null;
  return (
    <details className={`text-sm ${scuro ? 'text-white/80' : 'text-ink/70'}`}>
      <summary className="cursor-pointer">
        <b>{ore(b.ore_mese)} al mese</b>
        {b.ore_per_dipendente !== null ? ` · ${ore(b.ore_per_dipendente)} per persona` : ''}
        {b.euro_anno !== null ? ` · ${euroAnno(b.euro_anno)}` : ''} <span className="text-xs opacity-70">({STIMA[b.stima]})</span>
      </summary>
      <p className="mt-1 text-xs opacity-80">Come si calcola: {b.formula}.</p>
    </details>
  );
}


/** Il ruolo di chi guarda (contratto, fase 1): orienta diagnosi, dashboard e piano. */
function Ruolo({ d }: { d: ContractSummaryDto }) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const [scelta, setScelta] = useState(d.ruolo_cliente?.ruolo ?? '');
  const salva = useMutation({
    mutationFn: (ruolo: string) => api<ContractSummaryDto>(t('/onboarding/contratto/ruolo'), { method: 'POST', body: { ruolo } }),
    onSuccess: (x) => qc.setQueryData(['tenant', tenant.id, 'onboarding', 'contratto'], x),
  });
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-ink/60">Guardi la tua azienda come</span>
      <select className="rounded-xl border border-black/10 bg-white px-3 py-1.5" value={scelta} onChange={(e) => setScelta(e.target.value)}>
        <option value="">scegli il tuo ruolo…</option>
        {d.ruoli.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </select>
      <button className="rounded-xl bg-accent px-3 py-1.5 text-white disabled:opacity-40" disabled={!scelta || scelta === d.ruolo_cliente?.ruolo || salva.isPending} onClick={() => salva.mutate(scelta)}>
        Conferma
      </button>
      {d.ruolo_cliente && !d.ruolo_cliente.confermato && <span className="text-xs text-amber-700">il titolare deve confermarlo</span>}
      <ErrorText>{salva.error ? errorMessage(salva.error) : ''}</ErrorText>
    </div>
  );
}

const STATO_ATTR = { da_confermare: 'da confermare con te', da_collegare: 'fonte da collegare' } as const;

/** Il cliente scrive un attributo che solo lui conosce (contratto, fase 2): vale come fonte sua, mai stimato. */
function Conferma({ sede, attributo }: { sede: string; attributo: string }) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const [v, setV] = useState('');
  const salva = useMutation({
    mutationFn: () => api<ContractSummaryDto>(t('/onboarding/contratto/sede'), { method: 'POST', body: { sede_id: sede, attributo, valore: v } }),
    onSuccess: (x) => qc.setQueryData(['tenant', tenant.id, 'onboarding', 'contratto'], x),
  });
  return (
    <span className="flex items-center gap-1">
      <input className="w-40 rounded-lg border border-black/10 px-2 py-0.5 text-xs" placeholder="scrivilo tu" value={v} onChange={(e) => setV(e.target.value)} />
      <button className="rounded-lg px-2 py-0.5 text-xs ring-1 ring-black/10 disabled:opacity-40" disabled={!v.trim() || salva.isPending} onClick={() => salva.mutate()}>
        Salva
      </button>
    </span>
  );
}

/** Gli attributi di ogni sede (contratto, fase 2): ciò che si sa, con la fonte, e ciò che manca. */
function Sedi({ xs }: { xs: SiteAttributesDto[] }) {
  if (!xs.length) return null;
  return (
    <section className={BOX}>
      <h2 className="text-lg font-semibold">Com’è fatta ogni sede</h2>
      {xs.map((s) => (
        <details key={s.sede_id} className="rounded-2xl ring-1 ring-black/5">
          <summary className="flex cursor-pointer flex-wrap items-baseline justify-between gap-2 px-4 py-3 text-sm">
            <span className="font-medium">{s.sede}</span>
            <span className="text-xs text-ink/50">{Math.round(s.trovati * 100)}% trovato dalle fonti</span>
          </summary>
          <table className="mx-4 mb-3 text-sm">
            <tbody>
              {s.attributi.map((a, i) => (
                <tr key={i} className="align-top">
                  <th className="w-40 py-0.5 pr-3 text-left font-normal text-ink/50">{a.attributo}</th>
                  <td className="py-0.5">
                    {a.valore ? (
                      <>
                        {a.url ? (
                          <a className="text-accent hover:underline" href={a.url} target="_blank" rel="noreferrer">
                            {a.valore}
                          </a>
                        ) : (
                          a.valore
                        )}
                        {a.grado && <span className="ml-1 text-xs text-ink/40">({SOURCE_GRADE_TEXT[a.grado]})</span>}
                      </>
                    ) : a.stato === 'da_confermare' ? (
                      <Conferma sede={s.sede_id} attributo={a.attributo} />
                    ) : (
                      <span className="text-xs text-ink/40">{STATO_ATTR[a.stato as keyof typeof STATO_ATTR]}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ))}
    </section>
  );
}

/** I processi aziendali (contratto, fase 5): come l'azienda crea valore e cosa lo sostiene, fase per fase. */
function Processi({ xs }: { xs: ProcessDto[] }) {
  if (!xs.length) return null;
  const blocco = (titolo: string, ys: ProcessDto[]) =>
    ys.length > 0 && (
      <div className="space-y-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink/50">{titolo}</h3>
        {ys.map((p) => (
          <details key={`${p.tipo}:${p.nome}`} className="rounded-2xl ring-1 ring-black/5">
            <summary className="flex cursor-pointer flex-wrap items-baseline justify-between gap-2 px-4 py-3 text-sm">
              <span className="font-medium">{p.nome}</span>
              <span className="text-xs text-ink/50">
                {p.fasi.length} {p.tipo === 'primario' ? 'fasi' : 'procedure'}
                {p.responsabile ? ` · ne risponde ${p.responsabile}` : ' · responsabile da assegnare'}
                {p.sedi.length ? ` · ${p.sedi.join(', ')}` : ''}
              </span>
            </summary>
            <ol className="space-y-1 px-4 pb-3 text-sm">
              {p.fasi.map((f, i) => (
                <li key={f.nome} className="flex flex-wrap gap-x-2">
                  <span>
                    {i + 1}. {f.nome}
                  </span>
                  {f.ruoli.length > 0 && <span className="text-xs text-ink/50">· {f.ruoli.join(', ')}</span>}
                  {f.programmi.length > 0 && <span className="text-xs text-ink/40">· {f.programmi.join(', ')}</span>}
                  {(f.entra || f.esce) && (
                    <span className="w-full pl-4 text-xs text-ink/50">
                      {f.entra ? `entra: ${f.entra}` : ''}
                      {f.entra && f.esce ? ' · ' : ''}
                      {f.esce ? `esce: ${f.esce}` : ''}
                    </span>
                  )}
                </li>
              ))}
            </ol>
            {p.obblighi.length > 0 && <p className="px-4 pb-3 text-xs text-ink/50">Obblighi che realizza: {p.obblighi.join('; ')}</p>}
          </details>
        ))}
      </div>
    );
  return (
    <section className={BOX}>
      <h2 className="text-lg font-semibold">Come lavora la tua azienda: i processi</h2>
      {blocco('Ciò che fa per i clienti', xs.filter((p) => p.tipo === 'primario'))}
      {blocco('Ciò che lo sostiene', xs.filter((p) => p.tipo === 'supporto'))}
    </section>
  );
}

const FAMIGLIA: Record<ProgramDto['famiglia'], { titolo: string; nota: string }> = {
  piattaforma_pubblica: { titolo: 'Verso lo Stato e gli enti', nota: 'le piattaforme pubbliche che la legge chiede, con la norma' },
  processi: { titolo: 'Dentro i processi', nota: 'i programmi con cui lavorano le attività e le sedi' },
  quotidiano: { titolo: 'Ogni giorno', nota: 'gli strumenti d’ufficio di tutta l’impresa' },
};

/** I programmi in tre famiglie (contratto, fase 7), con chi ne risponde. */
function Programmi({ xs }: { xs: ProgramDto[] }) {
  if (!xs.length) return null;
  return (
    <section className={BOX}>
      <h2 className="text-lg font-semibold">Con quali programmi lavora</h2>
      <div className="grid gap-3 md:grid-cols-3">
        {(Object.keys(FAMIGLIA) as Array<ProgramDto['famiglia']>).map((f) => {
          const ys = xs.filter((p) => p.famiglia === f);
          return (
            <div key={f} className="rounded-2xl p-4 ring-1 ring-black/5">
              <h3 className="font-medium">{FAMIGLIA[f].titolo}</h3>
              <p className="text-xs text-ink/50">{FAMIGLIA[f].nota}</p>
              <ul className="mt-2 space-y-1.5 text-sm">
                {ys.length ? (
                  ys.map((p) => (
                    <li key={`${p.nome}|${p.dove}`}>
                      {p.servizio ?? p.nome}
                      {p.prodotto && <span className="text-ink/60"> · {p.prodotto}</span>}
                      <span className="block text-xs text-ink/50">
                        {[p.servizio ? p.nome : null, p.norma, p.dove !== 'tutta l’impresa' ? p.dove : null, p.risponde ? `ne risponde ${p.risponde}` : 'responsabile da assegnare'].filter(Boolean).join(' · ')}
                      </span>
                      {(p.accesso || p.delega || p.sanzione) && (
                        <span className="block text-xs text-ink/50">
                          {[p.accesso ? `si entra con ${p.accesso}` : null, p.delega ? `di solito con delega a ${p.delega}` : null, p.sanzione ? `se non si fa: ${p.sanzione}` : null].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </li>
                  ))
                ) : (
                  <li className="text-xs text-ink/40">nessuno</li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Diagnosi({ d }: { d: ContractSummaryDto['diagnosi'] }) {
  const con = d.reparti.filter((r) => r.scarti.length);
  if (!con.length && !d.azienda.length) return null;
  return (
    <section className={BOX}>
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold">Dove la tua azienda è lontana dall’ideale</h2>
        {d.totale && <Benefici b={d.totale} />}
      </header>
      {d.azienda.length > 0 && (
        <ul className="space-y-1 text-sm">
          {d.azienda.map((g, i) => (
            <li key={i}>
              <span className="mr-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">{GAP[g.tipo]}</span>
              {g.testo}
            </li>
          ))}
        </ul>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        {con.map((r) => (
          <div key={`${r.sede_id}:${r.reparto_id}`} className="rounded-2xl p-4 ring-1 ring-black/5">
            <h3 className="font-medium">
              {r.reparto} <span className="text-xs font-normal text-ink/50">· {r.sede}</span>
            </h3>
            <ul className="mt-1 space-y-1 text-sm text-ink/80">
              {r.scarti.map((g, i) => (
                <li key={i}>
                  <span className="mr-1 text-xs text-ink/50">{GAP[g.tipo]}:</span>
                  {g.testo}
                </li>
              ))}
            </ul>
            <div className="mt-2">
              <Benefici b={r.benefici} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Righe({ titolo, xs }: { titolo: string; xs: RoleDashboardLineDto[] }) {
  if (!xs.length) return null;
  return (
    <div>
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-white/50">{titolo}</h4>
      <ul className="mt-1 space-y-1 text-sm">
        {xs.map((x, i) => (
          <li key={i} className="flex justify-between gap-3">
            <span>{x.testo}</span>
            {x.quando && <span className="shrink-0 text-xs text-white/50">{x.quando}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Le dashboard d'esempio (contratto, fase 12): come lavorerebbe con Timonio chi ricopre ogni ruolo. */
function Dashboard({ xs }: { xs: RoleDashboardDto[] }) {
  const [i, setI] = useState(0);
  const d = xs[Math.min(i, xs.length - 1)];
  if (!d) return null;
  return (
    <section className={BOX}>
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold">Timonio al lavoro, ruolo per ruolo</h2>
        <select className="rounded-xl border border-black/10 bg-white px-3 py-1.5 text-sm" value={i} onChange={(e) => setI(Number(e.target.value))}>
          {xs.map((x, k) => (
            <option key={x.ruolo} value={k}>
              {x.ruolo}
              {x.reparto ? ` · ${x.reparto}` : ''}
            </option>
          ))}
        </select>
      </header>
      <div className="rounded-3xl bg-[#0d1220] p-5 text-[#eef2ff] md:p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-base font-semibold">
            Buongiorno · {d.ruolo}
            {d.sede ? <span className="font-normal text-white/50"> · {d.sede}</span> : null}
          </p>
          <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs">esempio con i dati della tua azienda</span>
        </div>
        {d.inefficienza && <p className="mt-2 text-sm text-amber-200">Oggi senza Timonio: {d.inefficienza}</p>}
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Righe titolo="Oggi" xs={d.oggi} />
          <Righe titolo="Scadenze e obblighi" xs={d.scadenze} />
          <Righe titolo="Cosa fa Timonio per te" xs={d.agente} />
          <Righe titolo="Procedure" xs={d.procedure.slice(0, 6)} />
        </div>
        {d.programmi.length > 0 && <p className="mt-4 text-xs text-white/60">Programmi: {d.programmi.join(', ')}</p>}
        <div className="mt-3">
          <Benefici b={d.benefici} scuro />
        </div>
        {d.in_arrivo.length > 0 && <p className="mt-2 text-xs text-white/50">In arrivo: {d.in_arrivo.join('; ')}</p>}
      </div>
    </section>
  );
}

const SCADENZA: Record<PlanGoalDto['scadenza_giorni'], string> = { 30: 'entro 30 giorni', 60: 'entro 60 giorni', 90: 'entro 90 giorni' };
const STATO: Record<PlanGoalDto['stato'], string> = { proposto: '', accettato: 'Accettato', rifiutato: 'Rifiutato', rimandato: 'Rimandato' };

/** Il piano degli obiettivi (contratto, fase 13): accettare un obiettivo accetta la proposta da cui nasce. */
function Piano({ xs }: { xs: PlanGoalDto[] }) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const rispondi = useMutation({
    mutationFn: ({ id, decisione }: { id: string; decisione: 'accettata' | 'rifiutata' | 'rimandata' }) => api(t(`/onboarding/plant/proposals/${id}/answer`), { method: 'POST', body: { decisione } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'onboarding'] }),
  });
  if (!xs.length) return null;
  return (
    <section className={BOX}>
      <h2 className="text-lg font-semibold">Gli obiettivi che ti proponiamo di raggiungere insieme</h2>
      <ol className="space-y-3">
        {xs.map((o, k) => (
          <li key={o.id} className="rounded-2xl p-4 ring-1 ring-black/5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-medium">
                {k + 1}. {o.titolo} <span className="text-xs font-normal text-ink/50">· {o.reparto}{o.sede ? `, ${o.sede}` : ''} · {SCADENZA[o.scadenza_giorni]}</span>
              </h3>
              {o.stato !== 'proposto' ? (
                <span className="text-xs text-ink/60">{STATO[o.stato]}</span>
              ) : (
                <span className="flex gap-2">
                  <button className="rounded-xl bg-accent px-3 py-1 text-sm text-white" disabled={rispondi.isPending} onClick={() => rispondi.mutate({ id: o.proposta_id, decisione: 'accettata' })}>
                    Partiamo
                  </button>
                  <button className="rounded-xl px-3 py-1 text-sm ring-1 ring-black/10" disabled={rispondi.isPending} onClick={() => rispondi.mutate({ id: o.proposta_id, decisione: 'rimandata' })}>
                    Più avanti
                  </button>
                </span>
              )}
            </div>
            <dl className="mt-2 grid gap-1 text-sm text-ink/75 sm:grid-cols-[8rem_1fr]">
              <dt className="text-ink/50">Oggi</dt>
              <dd>{o.partenza}</dd>
              <dt className="text-ink/50">Traguardo</dt>
              <dd>{o.traguardo}</dd>
              <dt className="text-ink/50">Fa Timonio</dt>
              <dd>{o.fa_timonio}</dd>
              <dt className="text-ink/50">Fa l’azienda</dt>
              <dd>{o.fa_azienda}</dd>
              <dt className="text-ink/50">Come si misura</dt>
              <dd>{o.misura}</dd>
            </dl>
            <div className="mt-2">
              <Benefici b={o.benefici} />
            </div>
          </li>
        ))}
      </ol>
      <ErrorText>{rispondi.error ? errorMessage(rispondi.error) : ''}</ErrorText>
    </section>
  );
}

const SCOPO: Record<string, string> = {
  configurazione_ideale: 'progetto dell’azienda ideale',
  configurazione_ricerca_online: 'ricerca online',
  configurazione_ricerca_sede: 'ricerca delle sedi',
  configurazione_bilancio: 'lettura del bilancio',
  configurazione_compilazione: 'compilazione dei dati',
  onboarding_documento: 'lettura dei documenti',
  configurazione_efficientamento: 'proposte di efficientamento',
  piano_azienda: 'piano dell’azienda',
  impianti_riferimento: 'impianti',
};

/** Quanto è costata la configurazione (contratto, art. 11), accanto all'obiettivo di 4-6 €. */
function Costo({ c }: { c: NonNullable<ContractSummaryDto['costo']> }) {
  if (!c.chiamate) return null;
  const dentro = c.totale_eur <= c.obiettivo_eur.max;
  return (
    <details className="text-xs text-ink/50">
      <summary className="cursor-pointer">
        Costo del motore per questa azienda: <b className={dentro ? 'text-emerald-700' : 'text-amber-700'}>{c.totale_eur.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €</b> in {c.chiamate} chiamate · obiettivo
        del contratto {c.obiettivo_eur.min}-{c.obiettivo_eur.max} € a configurazione
      </summary>
      <ul className="mt-1 space-y-0.5">
        {c.per_scopo.slice(0, 10).map((x) => (
          <li key={x.scopo}>
            {SCOPO[x.scopo] ?? x.scopo.replace(/_/g, ' ')}: {x.eur.toLocaleString('it-IT', { minimumFractionDigits: 2 })} € ({x.chiamate})
          </li>
        ))}
      </ul>
    </details>
  );
}

function Misura({ xs }: { xs: ContractSummaryDto['misura'] }) {
  if (!xs.length) return null;
  return (
    <section className={BOX}>
      <h2 className="text-lg font-semibold">Promesso e misurato</h2>
      <ul className="space-y-2 text-sm">
        {xs.map((m) => (
          <li key={m.obiettivo_id} className="flex flex-wrap justify-between gap-2">
            <span>{m.titolo}</span>
            <span className="text-ink/60">
              promesse {ore(m.promesso_ore_mese)} al mese · misurate {m.misurato_ore_mese === null ? '—' : ore(m.misurato_ore_mese)}
            </span>
            <span className="w-full text-xs text-ink/50">{m.nota}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Le fasi 1 e 10-14 del contratto n. 1 in «La tua azienda» (ADR 0157): il ruolo di chi guarda, la diagnosi, la
 * dashboard d'esempio di ogni ruolo, il piano degli obiettivi e la misura nel tempo. Tutto composto dal server.
 */
export function Contratto() {
  const q = useTenantQuery<ContractSummaryDto>(['onboarding', 'contratto'], '/onboarding/contratto', { refetchInterval: (d) => (d?.in_preparazione ? 8000 : false) });
  const d = q.data;
  if (!d) return null;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Ruolo d={d} />
        <Documento />
      </div>
      {d.in_preparazione && <p className="text-sm text-ink/60">Sto ancora scrivendo le proposte reparto per reparto: diagnosi e obiettivi si completano da soli.</p>}
      <Sedi xs={d.sedi} />
      <Processi xs={d.processi} />
      <Programmi xs={d.programmi} />
      <Diagnosi d={d.diagnosi} />
      <Dashboard xs={d.dashboard} />
      <Piano xs={d.piano} />
      <Misura xs={d.misura} />
      {d.costo && <Costo c={d.costo} />}
    </div>
  );
}
